import { css } from '@emotion/react';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import {
  useCallback, useMemo, useState,
} from 'react';
import { AutocompleteComponent } from '~/_shared/baseComponents/autocomplete';
import { CheckboxComponent } from '~/_shared/baseComponents/checkbox';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { InputSize } from '~/_shared/baseComponents/inputs';
import {
  TooltipComponent, TooltipPlacement,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { copy } from '~/_shared/utils/collections/collections';
import { useTranslation } from '~/_shared/utils/hooks';
import { useGetColumnName } from '~/_shared/utils/spreadsheet/useColumnName';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { isLocationCount } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/modalSteps/constraintsValidation/constraints.helpers';

export const SELECTABLE_METRIC_LINE_SIZE = 36;
const DELAY_IN_MS = 500;
const MAX_SELECTABLE_METRICS = 3;
const MAX_VISIBLE_METRICS = 0;

enum MetricState {
  disabled= 'disabled',
  limitReached= 'limitReached',
  locationCount= 'locationCount',
  selectable= 'selectable',
}

const groupColumnItemStyle = (theme: Theme) => css({
  alignItems: 'center',
  backgroundColor: theme.backgroundColors.primary,
  borderTop: `1px solid ${theme.borderColors.primary}`,
  boxSizing: 'border-box',
  color: theme.textColors.primary,
  cursor: 'default',
  display: 'flex',
  fontSize: 14,
  fontWeight: 500,
  height: SELECTABLE_METRIC_LINE_SIZE,
  lineHeight: '16px',
  padding: '0 16px',

  '&:first-of-type': {
    borderTop: 'none',
  },
});

const simpleColumnItemStyle = (theme: Theme) => css({
  justifyContent: 'space-between',
}, groupColumnItemStyle(theme));

const labelStyle = (disabled: boolean) => (theme: Theme) => css({
  opacity: disabled ? theme.opacity.disabled : undefined,
});

const gapStyle = css({
  alignItems: 'center',
  display: 'flex',
  gap: 8,
});

const fullWidthStyle = css({ flex: 1 });

const dropdownWrapperStyle = css(fullWidthStyle, gapStyle, {
  flexDirection: 'row-reverse',
  minWidth: 0,
});

const autocompleteWrapperStyle = css({
  minWidth: 0,
  width: '100%',
});

export type SelectableMetricValue = {
  id: string;
  groupId?: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  numericPercent?: number;
};

type SelectableMetricsListComponentProps = {
  metrics: ReadonlyArray<SelectableMetricValue>;
  totalSelectedCount?: number;
  onChange: (ids: { metricId: string; groupIds?: ReadonlyArray<string>}, checked: boolean) => void;
};

export const SelectableMetricsListComponent = ({ metrics, totalSelectedCount, onChange }: SelectableMetricsListComponentProps) => {
  const [t] = useTranslation();
  const getColumnName = useGetColumnName();
  const [selectedGroupIds, setSelectedGroupIds] = useState<ReadonlySet<string>>(new Set());
  const EMPTY_VALUE_LABEL = `(${t('Empty')})`;

  const groupedMetrics = useMemo(() => {
    const grouped = new Map<string, SelectableMetricValue[]>();

    metrics.forEach(metric => {
      const existingMetrics = grouped.get(metric.id);
      if (existingMetrics) {
        existingMetrics.push(metric);
      }
      else {
        grouped.set(metric.id, [metric]);
      }
    });

    return Array.from(grouped.entries()); // [columnId, metrics[]]
  }, [metrics]);

  const limitReached = (totalSelectedCount ?? 0) >= MAX_SELECTABLE_METRICS;

  const handleGroupMetricsChange = useCallback(
    (metricGroup: ReadonlyArray<SelectableMetricValue>) => (selectedGroupIds: string[]) => {
      const currentValues = getAutocompleteValues(metricGroup);

      if (limitReached && selectedGroupIds.length > currentValues.length) {
        return;
      }

      const columnId = metricGroup[0]?.id;
      if (columnId) {
        onChange({ metricId: columnId, groupIds: selectedGroupIds }, true);
      }
    }, [limitReached, onChange]);

  const onCheckedSetter = useCallback((columnId: string) => {
    const isCurrentlyChecked = selectedGroupIds.has(columnId);
    setSelectedGroupIds(prevState => copy.andTogglePresence(prevState, columnId));

    if (isCurrentlyChecked) {
      onChange({ metricId: columnId, groupIds: [] }, true);
    }
  }, [onChange, selectedGroupIds]);

  return (
    <>
      {groupedMetrics.map(([columnId, metricGroup]) => {
        const isGroupMetric = metricGroup.length > 1;
        const columnName = getColumnName(columnId);

        if (isGroupMetric && columnName) {
          const orderedMetrics = [...metricGroup].sort(emptyLabelFirstComparator);

          const anyChecked = selectedGroupIds.has(columnId) && metricGroup.some(mg => mg.checked);
          const isDisabled = limitReached && !anyChecked;
          const currentColumnSelectedCount = metrics
            .filter(metric => metric.checked && metric.id === columnId)
            .length;
          const otherColumnsSelectedCount = (totalSelectedCount ?? 0) - currentColumnSelectedCount;

          return (
            <div css={groupColumnItemStyle} key={columnId} >
              <span css={[labelStyle(limitReached), fullWidthStyle]}>{columnName}</span>
              <span css={dropdownWrapperStyle}>
                {!isDisabled && (
                  <CheckboxComponent
                    isChecked={selectedGroupIds.has(columnId)}
                    checkedSetter={() => onCheckedSetter(columnId)}
                    ariaLabel={columnName}
                  />
                )}

                {isDisabled && (
                  <TooltipComponent
                    placement={TooltipPlacement.TopStart}
                    tooltipContent={t('boundaryAI.metrics.issue.maxLimitReached', { maxLimit: MAX_SELECTABLE_METRICS })}
                  >
                    <FontAwesomeIcon icon={faCircleInfo} />
                  </TooltipComponent>
                )}

                {selectedGroupIds.has(columnId) && !isDisabled && (
                  <div css={autocompleteWrapperStyle}>
                    <AutocompleteComponent
                      checkSelected
                      inPortal
                      inputSize={InputSize.Xs}
                      isClearable
                      maxSelectedVisible={MAX_VISIBLE_METRICS}
                      multiple
                      onChange={handleGroupMetricsChange(metricGroup)}
                      options={orderedMetrics.map(mg => ({ value: mg.groupId, name: mg.label || EMPTY_VALUE_LABEL }))}
                      selectionLimit={MAX_SELECTABLE_METRICS - otherColumnsSelectedCount}
                      value={getAutocompleteValues(metricGroup)}
                    />
                  </div>
                )}
              </span>
            </div>
          );
        }

        const metric = metricGroup[0];
        if (!metric) {
          return null;
        }

        const metricState = getMetricState(metric, limitReached);

        return (
          <div css={simpleColumnItemStyle} key={metric.id}>
            <span css={labelStyle(metric.disabled || limitReached)}>{metric.label}</span>
            <div css={gapStyle}>
              {metricState === MetricState.locationCount && (
                <TooltipComponent
                  delayShow={DELAY_IN_MS}
                  placement={TooltipPlacement.TopStart}
                  tooltipContent={t('boundaryAI.creationVariables.locationCount')}
                >
                  <FontAwesomeIcon icon={faCircleInfo} />
                </TooltipComponent>
              )}

              {(metricState === MetricState.selectable || metricState === MetricState.locationCount) && (
                <CheckboxComponent
                  isChecked={metric.checked}
                  checkedSetter={() => onChange({ metricId: metric.id }, !metric.checked)}
                  ariaLabel={metric.label}
                />
              )}

              {metricState === MetricState.limitReached && (
                <TooltipComponent
                  placement={TooltipPlacement.TopStart}
                  tooltipContent={t('boundaryAI.metrics.issue.maxLimitReached', { maxLimit: MAX_SELECTABLE_METRICS })}
                >
                  <FontAwesomeIcon icon={faCircleInfo} />
                </TooltipComponent>
              )}

              {metricState === MetricState.disabled && (
                <div css={gapStyle}>
                  <span css={{ textTransform: 'uppercase' }}>{t('Text Column')}</span>
                  <TooltipComponent
                    placement={TooltipPlacement.TopStart}
                    tooltipContent={t('boundaryAI.metrics.issue.nonNumerical', {
                      percentage: Math.min(99, Math.floor(100 - (metric.numericPercent ?? 0))),
                    })}
                  >
                    <FontAwesomeIcon icon={faCircleInfo} />
                  </TooltipComponent>
                </div>
              )}
            </div>
          </div>
        );
      })
      }
    </>
  );
};

const getAutocompleteValues = (
  metricGroup: ReadonlyArray<SelectableMetricValue>
): string[] => metricGroup
  .filter((m): m is SelectableMetricValue & { groupId: string } => m.checked && notNullsy(m.groupId))
  .map(m => m.groupId);

const emptyLabelFirstComparator = (a: SelectableMetricValue, b: SelectableMetricValue) => {
  const aHasLabel = !!a.label;
  const bHasLabel = !!b.label;

  if (!aHasLabel && bHasLabel) {
    return -1;
  }
  if (aHasLabel && !bHasLabel) {
    return 1;
  }
  return 0;
};

const getMetricState = (metric: SelectableMetricValue, isLimitReached: boolean) => {
  if (isLimitReached && !metric.checked) {
    return MetricState.limitReached;
  }
  if (isLocationCount(metric.id)) {
    return MetricState.locationCount;
  }
  if (metric.disabled) {
    return MetricState.disabled;
  }
  return MetricState.selectable;
};
